import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const { t, lang } = useLang();

  useEffect(() => {
    if (!success || !sessionId) return;
    let alive = true;
    const poll = async () => {
      if (!alive) return;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        setAttempts((a) => a + 1);
      } catch {
        setAttempts((a) => a + 1);
      }
    };
    poll();
    const interval = setInterval(() => {
      setAttempts((a) => {
        if (a >= 8) {
          clearInterval(interval);
          setStatus("timeout");
          return a;
        }
        poll();
        return a;
      });
    }, 2000);
    return () => { alive = false; clearInterval(interval); };
  }, [success, sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-alabaster" data-testid="payment-result">
      <div className="max-w-lg text-center px-6">
        {!success ? (
          <>
            <XCircle size={64} className="mx-auto text-terracotta mb-6" />
            <h1 className="font-serif text-4xl text-navy mb-4">{lang === "fr" ? "Paiement annulé" : "Payment cancelled"}</h1>
            <p className="text-navy/70 mb-6">{lang === "fr" ? "Aucun problème, votre paiement n'a pas été traité." : "No worries — your payment wasn't processed."}</p>
            <Link to="/tcf" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm">{lang === "fr" ? "Retour à la formation" : "Back to training"}</Link>
          </>
        ) : status === "paid" ? (
          <>
            <CheckCircle2 size={64} className="mx-auto text-terracotta mb-6" data-testid="payment-success-icon" />
            <h1 className="font-serif text-5xl text-navy mb-4">{lang === "fr" ? "Bienvenue !" : "Welcome!"}</h1>
            <p className="text-navy/70 mb-6">{lang === "fr" ? "Votre paiement est confirmé, votre cours vous attend." : "Payment confirmed. Your course is ready."}</p>
            <Link to="/dashboard" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm hover:bg-terracotta transition">{t("nav.dashboard")}</Link>
          </>
        ) : status === "expired" || status === "timeout" ? (
          <>
            <XCircle size={64} className="mx-auto text-terracotta mb-6" />
            <h1 className="font-serif text-4xl text-navy mb-4">{lang === "fr" ? "Session expirée" : "Session expired"}</h1>
            <p className="text-navy/70 mb-6">{lang === "fr" ? "Réessayez ou contactez-nous." : "Please try again or contact us."}</p>
            <Link to="/tcf" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm">{lang === "fr" ? "Retour à la formation" : "Back to training"}</Link>
          </>
        ) : (
          <>
            <Loader2 size={48} className="mx-auto text-navy animate-spin mb-6" />
            <h1 className="font-serif text-3xl text-navy mb-2">{lang === "fr" ? "Vérification du paiement…" : "Verifying payment…"}</h1>
            <p className="text-navy/60 text-sm">{lang === "fr" ? "Cela prend quelques secondes." : "This takes a few seconds."}</p>
          </>
        )}
      </div>
    </div>
  );
}
